/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { runTask } from 'ember-lifeline';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { getCancelPriceEditRouteFor } from 'uplisting-frontend/utils/rhs/routes';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingPriceEditSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiBookingPriceEditComponent extends Component<BookingPriceEditSignature> {
  @service store!: Services['store'];
  @service router!: Services['router'];
  @service session!: Services['session'];
  @service notifications!: Services['notifications'];

  @service('repositories/price')
  priceRepository!: Services['repositories/price'];

  @tracked showModal = true;

  @tracked isSavingPrices = false;
  @tracked isDiscountChanged = false;
  @tracked isChargeChanged = false;

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['price']> {
    return this.priceRepository.buildChangeset(this.booking.price);
  }

  @cached
  get cancelPriceEditRoute(): string {
    return getCancelPriceEditRouteFor(this.router.currentRouteName ?? '');
  }

  @action
  handleChangeDiscount(
    discount: ModelRegistry['extra-price'],
    value: number,
  ): void {
    this.isDiscountChanged = true;

    discount.amount = value;
  }

  @action
  handleChangeCharge(
    charge: ModelRegistry['extra-price'],
    value: number,
  ): void {
    this.isChargeChanged = true;

    charge.amount = value;
  }

  @action
  handleCloseModal(): void {
    if (this.isDiscountChanged) {
      this.booking.price.discounts.forEach((d) => d.rollbackAttributes());
    }

    if (this.isChargeChanged) {
      this.booking.price.charges.forEach((c) => c.rollbackAttributes());
    }

    this.changeset.rollback();

    this.showModal = false;

    // waiting for the modal animation to complete(modal closing animation takes 0.3s to complete)
    runTask(
      this,
      () => {
        this.router.transitionTo(this.cancelPriceEditRoute);
      },
      300,
    );
  }

  @action
  @waitFor
  async handleFormSubmit(): Promise<void> {
    const { changeset } = this;

    const oldBalance = changeset.hostEarnings as number;

    this.isSavingPrices = true;
    changeset.hostEdited = true;
    changeset.checkIn = this.booking.checkIn;
    changeset.checkOut = this.booking.checkOut;

    try {
      const price = await changeset.save();

      this.notifications.info(
        'action_bookings_price_edit.modal.notifications.applied',
      );

      this.updateBookingPrice(oldBalance, price.hostEarnings as number);

      this.handleCloseModal();
    } catch {
      this.notifications.error();
    } finally {
      this.isSavingPrices = false;
      this.isDiscountChanged = false;
      this.isChargeChanged = false;
    }
  }

  private updateBookingPrice(oldBalance: number, newBalance: number) {
    const diff = newBalance - oldBalance;

    if (diff === 0) {
      return;
    }

    const { store, currentUser } = this;

    const record = store.createRecord('history-booking-price', {
      amount: diff,
      createdAt: new Date(),
      userEmail: currentUser.email,
      userName: currentUser.name,
    });

    this.booking.priceChanges.push(record);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price::Edit': typeof UiBookingPriceEditComponent;
  }
}
